/** Same as `--large-up` in postcss.config.js */
export const LARGE_UP = "only screen and (width >= 59.25rem)"

export class DesktopDropdowns {
  private cssClass: string
  private abortController = new AbortController()
  private largeUpQuery = window.matchMedia(LARGE_UP)

  constructor(cssClass: string) {
    this.cssClass = cssClass
    this.addEventListeners()
  }

  private isDesktop(): boolean {
    return this.largeUpQuery.matches
  }

  private navItems(): NodeListOf<Element> {
    return document.querySelectorAll(`.${this.cssClass}__nav-container .${this.cssClass}__nav-item`)
  }

  private addEventListeners(): void {
    const { signal } = this.abortController

    this.navItems().forEach((navItem) => {
      const button = navItem.querySelector("button")
      const dropdownId = button?.getAttribute("aria-controls")
      const dropdown = dropdownId ? document.getElementById(dropdownId) : null

      if (!dropdown || !button) return

      dropdown.querySelectorAll("a, button").forEach((item) => {
        if (!item.hasAttribute("tabindex")) {
          item.setAttribute("tabindex", "0")
        }
      })

      const expandDropdown = () => {
        dropdown.classList.add("active")
        button.setAttribute("aria-expanded", "true")
        dropdown.setAttribute("aria-hidden", "false")
      }

      const collapseDropdown = () => {
        dropdown.classList.remove("active")
        button.setAttribute("aria-expanded", "false")
        dropdown.setAttribute("aria-hidden", "true")
      }

      const ifDesktop = (handler: () => void) => () => {
        if (this.isDesktop()) handler()
      }

      navItem.addEventListener("mouseenter", ifDesktop(expandDropdown), { signal })
      navItem.addEventListener("mouseleave", ifDesktop(collapseDropdown), { signal })
      button.addEventListener("focus", ifDesktop(expandDropdown), { signal })
      dropdown.addEventListener("mouseenter", ifDesktop(expandDropdown), { signal })
      dropdown.addEventListener("focusin", ifDesktop(expandDropdown), { signal })

      button.addEventListener(
        "keydown",
        (event) => {
          if (!this.isDesktop() || (event.key !== "Enter" && event.key !== " ")) return

          event.preventDefault()
          if (button.getAttribute("aria-expanded") === "true") {
            collapseDropdown()
            button.focus()
          } else {
            expandDropdown()
          }
        },
        { signal },
      )

      dropdown.addEventListener(
        "focusout",
        ifDesktop(() => {
          setTimeout(() => {
            const { activeElement } = document
            if (!dropdown.contains(activeElement) && !button.contains(activeElement)) {
              collapseDropdown()
            }
          }, 200)
        }),
        { signal },
      )
    })

    this.largeUpQuery.addEventListener(
      "change",
      (event) => {
        if (!event.matches) this.collapseAll()
      },
      { signal },
    )
  }

  private collapseAll(): void {
    this.navItems().forEach((navItem) => {
      const button = navItem.querySelector("button")
      const dropdownId = button?.getAttribute("aria-controls")
      const dropdown = dropdownId ? document.getElementById(dropdownId) : null

      if (dropdown && button) {
        dropdown.classList.remove("active")
        button.setAttribute("aria-expanded", "false")
        dropdown.setAttribute("aria-hidden", "true")
      }
    })
  }

  destroy(): void {
    this.abortController.abort()
  }
}
